import '@webcomponents/shadydom';
import 'whatwg-fetch';
import signup from './signup.html';
import signupTranslate from './signup-translate.js';
import login from './login.html';
import loginTranslate from './login-translate.js';
import errorTranslate from './error-translate.js';

const langToLang = {
  tc: 'zho',
  sc: 'chi',
  en: 'eng'
};

function getErrorMessage(element, lang) {
  if (element.tagName === 'SELECT') return errorTranslate[lang].notSelected.replace('{{name}}', element.dataset.name);
  else if (element.value.length === 0) {
    if (element.name === 'confirm_email') return errorTranslate[lang].confirmEmail;
    else return errorTranslate[lang].empty.replace(/{{name}}/, element.placeholder.replace(/ \*/g, '').toLowerCase());
  }
  else return errorTranslate[lang].invalid.replace(/{{name}}/, element.placeholder.replace(/ \*/g, ''));
};

function getInvalidFeedback(element) {
  return element.parentNode.getElementsByClassName('invalid-feedback')[0];
}

function setSelectRequired(select, required) {
  if (required) {
    select.disabled = false;
    select.required = true;
    select.querySelector('option[value=""]').innerText = select.querySelector('option[value=""]').innerText.replace(/ \*$/, '') + ' *';
  } else {
    select.disabled = true;
    select.required = false;
    select.querySelector('option[value=""]').innerText = select.querySelector('option[value=""]').innerText.replace(/ \*$/, '');
    select.value = '';
  }
}

function createModal(html, lang, translate, animate = true) {
  if (typeof translate[lang] === undefined) {
    throw 'Language not supported';
  }

  let output = html;

  for (const key in translate[lang]) {
    output = output.replace(new RegExp(`{{${key}}}`, 'g'), translate[lang][key]);
  }

  output = output.replace(new RegExp(`{{lang}}`, 'g'), langToLang[lang]);

  const modalContainer = document.createElement('div');
  document.body.style.overflow = 'hidden';
  modalContainer.id = 'modal';
  modalContainer.style.zIndex = 10000;
  modalContainer.style.position = 'fixed';
  modalContainer.style.top = 0;
  modalContainer.style.left = 0;
  modalContainer.style.width = '100%';
  modalContainer.style.height = '100%';
  modalContainer.style.transition = 'ease-in 400ms';
  modalContainer.style.overflow = 'auto';

  const shadow = modalContainer.attachShadow({ mode: 'open' });
  shadow.innerHTML = output;
  document.body.appendChild(modalContainer);

  const closeModal = () => {
    document.body.style.overflow = '';
    document.body.removeChild(modalContainer);
  };
  modalContainer.shadowRoot.querySelector('.close-btn').onclick = closeModal;

  if (animate) {
    modalContainer.style.opacity = 0;
    setTimeout(() => modalContainer.style.opacity = 1, 10);
  }
  return { modal: modalContainer.shadowRoot.querySelector('.container'), closeModal };
}

function initPhonePrefixButton(modal) {
  const phonePrefixButton = modal.getElementsByClassName('phone-prefix-button')[0];
  const phonePrefixDropdown = modal.getElementsByClassName('phone-prefix-dropdown')[0];

  phonePrefixButton.onclick = () => {
    if (phonePrefixDropdown.classList.contains('show')) phonePrefixDropdown.classList.remove('show');
    else phonePrefixDropdown.classList.add('show');
  };
  for (const dropdownItem of modal.getElementsByClassName('dropdown-item')) {
    dropdownItem.onclick = function (event) {
      const selectedPrefix = event.currentTarget.getElementsByTagName('span')[0].innerText;
      phonePrefixDropdown.classList.remove('show');
      phonePrefixButton.getElementsByTagName('img')[0].src = event.currentTarget.getElementsByTagName('img')[0].src;
      phonePrefixButton.getElementsByTagName('span')[0].innerText = selectedPrefix;
      modal.querySelector('input[name="country_code"]').value = `+${selectedPrefix}`;

      if (selectedPrefix === '86') {
        modal.querySelector('input[name=phone]').setAttribute('pattern', `1\\d{10}`);
      } else {
        modal.querySelector('input[name=phone]').setAttribute('pattern', `\\d{8}`);
      }
    };
  }
}

function initSendCodeButton(modal, lang, translate, action, apiPath) {
  const sendCodeButton = modal.querySelector('.send-code-button');

  sendCodeButton.onclick = (event) => {
    event.preventDefault();
    const phoneInput = modal.querySelector('input[name=phone]');

    if (phoneInput.validity.valid) {
      const formData = new FormData();
      const originalText = sendCodeButton.innerText;

      formData.append('country_code', modal.querySelector('input[name=country_code]').value);
      formData.append('phone', phoneInput.value);
      formData.append('lang', lang);
      formData.append('action', action);

      sendCodeButton.disabled = true;
      fetch(`${apiPath}/verification-code.php`, { method: 'post', body: formData, credentials: 'include' })
        .then(response => response.json())
        .then(json => {
          if (json.status !== 200) {
            alert(errorTranslate[lang].serverResponse[json.message] || errorTranslate[lang].serverResponse.unknownError);
            sendCodeButton.disabled = false;
          } else {
            sendCodeButton.innerText = translate[lang].codeSent;

            let countdown = 60;
            const interval = setInterval(() => {
              countdown--;
              sendCodeButton.innerText = `${translate[lang].codeSent} (${countdown})`;
              if (countdown === 0) {
                clearInterval(interval);
                sendCodeButton.disabled = false;
                sendCodeButton.innerText = originalText;
              }
            }, 1000);
          }
        });
    } else {
      phoneInput.parentNode.classList.add('was-validated');
      getInvalidFeedback(phoneInput).innerText = getErrorMessage(phoneInput, lang);
    }
  };
}

function initValidation(modal, lang, postDsetination, onSuccess) {
  const form = modal.querySelector('form');

  form.onsubmit = (event) => {
    event.preventDefault();
    const invalidInputs = form.querySelectorAll('input:invalid');
    if (invalidInputs.length === 0 && form.querySelectorAll('select:invalid').length === 0) {
      fetch(postDsetination, { method: 'post', body: new FormData(form), credentials: 'include' })
        .then(response => response.json())
        .then(json => {
          if (json.status === 200) onSuccess();
          else alert(errorTranslate[lang].serverResponse[json.message] || errorTranslate[lang].serverResponse.unknownError);
        });
    } else {
      form.classList.add('was-validated');
      for (const input of [...invalidInputs].filter(input => input.name !== 'accept_term_and_conditions')) {
        if (input.name !== 'gender') getInvalidFeedback(input).innerText = getErrorMessage(input, lang);
      }
    }
  };
}

const happyFansForm = {
  createSignup: (lang = 'en', apiPath, animate = true) => {
    const { modal } = createModal(signup, lang, signupTranslate, animate);
    initPhonePrefixButton(modal);
    initSendCodeButton(modal, lang, signupTranslate, 'signup', apiPath);
    initValidation(modal, lang, `${apiPath}/register.php`, () => {
      modal.querySelector('form').style.display = 'none';
      const registrationSuccess = modal.querySelector('#registration-success');
      registrationSuccess.style.display = 'block';
      registrationSuccess.getElementsByTagName('button')[0].onclick = () => window.location.reload();
    });

    const residenceSelect = modal.querySelector('select[name=residence]');
    const homeSelect = modal.querySelector('select[name=home_district]');
    const workSelect = modal.querySelector('select[name=work_district]');

    residenceSelect.onchange = () => {
      if (residenceSelect.value !== "2") {
        setSelectRequired(homeSelect, false);
        setSelectRequired(workSelect, false);
      } else {
        setSelectRequired(homeSelect, true);
        setSelectRequired(workSelect, true);
      }
    };

    const emailInput = modal.querySelector('input[name=email]');
    const confirmEmailInput = modal.querySelector('input[name=confirm_email]');
    const compareEmail = () => {
      if (emailInput.validity.valid && emailInput.value !== confirmEmailInput.value) {
        confirmEmailInput.setCustomValidity('invalid');
        getInvalidFeedback(confirmEmailInput).innerText = errorTranslate[lang].emailNotEquals;
      } else {
        confirmEmailInput.setCustomValidity('');
      }
    };
    emailInput.oninput = compareEmail;
    confirmEmailInput.oninput = compareEmail;

    const selects = [...modal.querySelectorAll('select')].filter(select => select.value === '');
    for (const select of selects) {
      const invalidFeedback = getInvalidFeedback(select);
      if (invalidFeedback) invalidFeedback.innerText = getErrorMessage(select, lang);
      select.addEventListener('change', () => {
        if (select.required && select.value === '') select.setCustomValidity('invalid');
        else select.setCustomValidity('');
      });
    }
  },
  createLogin: (lang = 'en', apiPath) => {
    const { modal, closeModal } = createModal(login, lang, loginTranslate);
    initPhonePrefixButton(modal);
    initSendCodeButton(modal, lang, loginTranslate, 'login', apiPath);
    initValidation(modal, lang, `${apiPath}/login.php`, () => window.location.reload());

    modal.querySelector('a').addEventListener('click', () => {
      closeModal();
      happyFansForm.createSignup(lang, apiPath, false);
    });
  },
  initHappyBuyMall: () => {
    document.addEventListener('DOMContentLoaded', () => {
      let lang = document.getElementsByClassName('header')[0].dataset.lang;
      fetch('../../api/member-info.php')
        .then(response => response.json())
        .then(json => {
          if (json.status === 200) {
            // Initialization for desktop and mobile
            for (const element of document.getElementsByClassName('member')) {
              element.classList.add('logged-in');
            }
            for (const element of document.querySelectorAll('.login-button, .signup-button, .member span.slash')) {
              element.style.display = 'none';
            }
            for (const memberName of document.getElementsByClassName('member-name')) {
              memberName.innerText = json.member['first_name'] + ' ' + json.member['last_name'];
            }
            for (const aTag of document.getElementsByClassName('url-to-member-profile')) {
              aTag.href = `https://user.happybuymall.com/?lang=${langToLang[lang]}&token=${json.token}`;
              // aTag.href = `https://happyfansclub.vtl-lab.com/?lang=${langToLang[lang]}&token=${json.token}`;
            }
            for (const logoutButton of document.getElementsByClassName('logout-button')) {
              logoutButton.onclick = () => {
                fetch('../../api/logout.php', { method: 'post' })
                  .then(response => {
                    if (response.status === 200) window.location.reload();
                    else response.text().then(text => alert(text));
                  });
              };
            }

            // Initialization for desktop
            const desktopMemberName = document.querySelector('.member.desktop-member>.member-container>a.member-name');
            const memberBackdrop = document.getElementsByClassName('member-action-backdrop')[0];
            const memberPopup = document.getElementsByClassName('member-action-popup')[0];

            desktopMemberName.addEventListener('click', () => {
              memberBackdrop.classList.add('show');
              memberPopup.classList.add('show');
            });
            memberBackdrop.addEventListener('click', () => {
              memberBackdrop.classList.remove('show');
              memberPopup.classList.remove('show');
            });
          } else {
            for (const loginButton of document.getElementsByClassName('login-button')) {
              loginButton.onclick = () => happyFansForm.createLogin(lang, '../../api');
            }
            for (const signupButton of document.getElementsByClassName('signup-button')) {
              signupButton.onclick = () => happyFansForm.createSignup(lang, '../../api');
            }
          }
        });
    });
  }
};

export { happyFansForm };
